/**
 * hkService — data access for Housekeeping Live (Oct 2026).
 *
 * Tables (migration 030): hk_rooms, hk_day_rooms, hk_messages, hk_uploads,
 * hk_activity. Clock columns on hk_day_rooms are overwritten by each upload;
 * the app-owned columns (status, overrides, guest gone, manual extras, late
 * checkout, ETA…) are never touched by an upload, so re-uploading during the
 * day keeps everything housekeeping and reception have already set.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from './supabase';
import { RULES } from './hkPriority';
import { parseHkReport, isChargesReport, parseChargesReport, extrasForArrivals, lateCheckoutsForDepartures } from './hkParser';

export const EXTRA_OPTIONS = ['Robes (spa guest)', 'Bubbly & chocolates', 'Flowers', 'Balloons', 'Split beds', 'Cot', 'Robes'];

export const TO_HK_KINDS = {
  guest_here: 'Guest here – do it next',
  late_checkout: 'Late checkout',
  add_extra: 'Add extra',
  ready_check: 'Is it ready?',
  note: 'Message',
};
export const TO_RECEPTION_KINDS = {
  guest_gone: 'Guest gone – check out in Clock',
  still_in: 'Guest still in room',
  dnd: 'Do not disturb',
  maintenance: 'Needs maintenance',
  missing: 'Item missing',
  ready: 'Room ready',
  note: 'Message',
};

/** Read an uploaded .xlsx File → { kind: 'hk' | 'charges', rows }. SheetJS loads on demand. */
export async function readClockFile(file) {
  const XLSX = await import('xlsx');
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array' });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null, raw: false });
  return { kind: isChargesReport(rows) ? 'charges' : 'hk', rows };
}

export function parseClockRows(kind, rows, day) {
  return kind === 'charges' ? parseChargesReport(rows) : parseHkReport(rows, day);
}

/** Save a parsed housekeeping report for a day. Returns { count, gone }. */
export async function saveUpload(day, parsed, fileName, userId) {
  const roomRows = parsed.rooms.map((r) => ({
    room: r.room, type: r.type, sector: r.sector, sector_key: r.sectorKey, premium: r.premium,
  }));
  let { error } = await supabase.from('hk_rooms').upsert(roomRows, { onConflict: 'room' });
  if (error) throw error;

  // A guest checked out in Clock drops out of Departures (confirmed 26 Oct
  // 2026, room 228), so a room that was departing on the last upload and no
  // longer is → guest gone.
  const { data: before } = await supabase.from('hk_day_rooms').select('room, departing, guest_gone').eq('day', day);
  const prev = Object.fromEntries((before || []).map((x) => [x.room, x]));
  const nowIso = new Date().toISOString();
  let gone = 0;

  const dayRows = parsed.rooms.map((r) => {
    const row = {
      day,
      room: r.room,
      job: r.job,
      clock_status: r.clockStatus,
      clock_note: r.clockNote,
      departing: r.departing,
      in_house: r.inHouse,
      expected: r.expected,
      eta: r.expected?.eta || null,
      comments: r.comments,
      extras_clock: r.extrasFromClock,
      late_checkout_clock: r.lateCheckout,
      flags: r.flags,
    };
    const p = prev[r.room];
    if (p && p.departing && !r.departing && !p.guest_gone) {
      row.guest_gone = true; row.guest_gone_at = nowIso; gone += 1;
    }
    return row;
  });
  // Rows that set guest_gone and rows that don't have different columns;
  // send them separately so the others keep whatever is already stored.
  const withGone = dayRows.filter((x) => x.guest_gone);
  const rest = dayRows.filter((x) => !x.guest_gone);
  for (const batch of [rest, withGone]) {
    if (!batch.length) continue;
    ({ error } = await supabase.from('hk_day_rooms').upsert(batch, { onConflict: 'day,room' }));
    if (error) throw error;
  }

  await supabase.from('hk_uploads').insert({
    day, file_name: fileName || null, rooms: parsed.rooms.length,
    check_ok: parsed.check.ok, check_messages: parsed.check.messages, uploaded_by: userId || null,
  });
  await logActivity(day, null, 'upload', { file: fileName, rooms: parsed.rooms.length, gone }, userId);
  return { count: parsed.rooms.length, gone };
}

/** Save the Clock Rooms Charges report: extras for guests arriving on `day`. */
export async function saveCharges(day, parsed, fileName, userId) {
  const byRoom = extrasForArrivals(parsed, day);
  const rooms = Object.keys(byRoom);
  const { data: existing, error: e1 } = await supabase.from('hk_day_rooms').select('room').eq('day', day);
  if (e1) throw e1;
  const have = new Set((existing || []).map((x) => x.room));

  // Rooms not yet known (housekeeping report not uploaded for that day yet)
  if (rooms.some((r) => !have.has(r))) {
    const masters = rooms.filter((r) => !have.has(r)).map((r) => ({ room: r, type: byRoom[r].type || 'Room' }));
    const { error } = await supabase.from('hk_rooms').upsert(masters, { onConflict: 'room', ignoreDuplicates: true });
    if (error) throw error;
  }
  for (const room of rooms) {
    const patch = { extras_charges: byRoom[room].extras, charges_raw: byRoom[room].raw };
    const q = have.has(room)
      ? supabase.from('hk_day_rooms').update(patch).eq('day', day).eq('room', room)
      : supabase.from('hk_day_rooms').insert({ day, room, job: 'vacant', ...patch });
    const { error } = await q;
    if (error) throw error;
  }
  // A re-upload is the full picture: clear extras on rooms no longer listed.
  const stale = [...have].filter((r) => !byRoom[r]);
  if (stale.length) {
    const { error } = await supabase.from('hk_day_rooms').update({ extras_charges: [], charges_raw: [] }).eq('day', day).in('room', stale);
    if (error) throw error;
  }
  // Late check-out is sold as an extra on the departing booking.
  const late = lateCheckoutsForDepartures(parsed, day);
  const lateRooms = Object.keys(late).filter((r) => have.has(r));
  if (lateRooms.length) {
    const { error } = await supabase.from('hk_day_rooms').update({ late_checkout_booked: true }).eq('day', day).in('room', lateRooms);
    if (error) throw error;
  }
  const noLate = [...have].filter((r) => !late[r]);
  if (noLate.length) {
    const { error } = await supabase.from('hk_day_rooms').update({ late_checkout_booked: false }).eq('day', day).in('room', noLate);
    if (error) throw error;
  }
  const withExtras = rooms.filter((r) => byRoom[r].extras.length);
  await supabase.from('hk_uploads').insert({
    day, file_name: `${fileName || 'charges'} (charges)`, rooms: rooms.length, check_ok: true, check_messages: [], uploaded_by: userId || null,
  });
  await logActivity(day, null, 'upload_charges', { file: fileName, arrivals: rooms.length, withExtras: withExtras.length }, userId);
  return { arrivals: rooms.length, withExtras, late: lateRooms };
}

export async function logActivity(day, room, action, detail, userId) {
  try { await supabase.from('hk_activity').insert({ day, room, action, detail: detail || null, actor: userId || null }); } catch (_) { /* log is best-effort */ }
}

/** Patch one room for a day (app-owned fields only) + activity log. */
export async function updateRoom(day, room, patch, userId, action) {
  const { error } = await supabase.from('hk_day_rooms').update(patch).eq('day', day).eq('room', room);
  if (error) throw error;
  logActivity(day, room, action || 'update', patch, userId);
}

export async function sendMessage({ day, room, direction, kind, body, userId }) {
  const { error } = await supabase.from('hk_messages').insert({
    day, room: room || null, direction, kind, body: body || null, created_by: userId || null,
  });
  if (error) throw error;
}

export async function ackMessage(id, userId) {
  const { error } = await supabase.from('hk_messages').update({ ack_at: new Date().toISOString(), ack_by: userId || null }).eq('id', id);
  if (error) throw error;
}

export async function doneMessage(id, userId) {
  const now = new Date().toISOString();
  const { error } = await supabase.from('hk_messages').update({ done_at: now, done_by: userId || null, ack_at: now }).eq('id', id).is('done_at', null);
  if (error) throw error;
}

/** DB row (+ room master) → the shape hkPriority expects. */
export function toLiveRoom(row, master) {
  const m = master || {};
  const extras = [...new Set([...(row.extras_charges || []), ...(row.extras_clock || []), ...(row.extras_manual || [])])];
  return {
    ...row,
    type: m.type || '',
    sector: m.sector || '',
    sectorKey: m.sector_key || 'other',
    premium: !!m.premium,
    eta: row.eta_manual || row.eta || null,
    // Paid LATE CHECK-OUT (€20) is always until 14:00 (Karthika, 27 Oct 2026)
    lateCheckout: row.late_checkout_manual || row.late_checkout_clock || (row.late_checkout_booked ? RULES.paidLateCheckout : null),
    lateBooked: !!row.late_checkout_booked,
    guestGone: !!row.guest_gone,
    guestWaiting: !!row.guest_waiting,
    earlyCheckin: !!row.early_checkin,
    overrideAt: row.override_at,
    holdReason: row.hold_reason,
    extras,
  };
}

/**
 * Live data for one day: rooms (merged with hk_rooms), messages, last upload.
 * Realtime on hk_day_rooms + hk_messages, plus a 60 s safety refetch.
 */
export function useHkDay(day) {
  const [rows, setRows] = useState([]);
  const [master, setMaster] = useState({});
  const [messages, setMessages] = useState([]);
  const [lastUpload, setLastUpload] = useState(null);
  const [lastCharges, setLastCharges] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const dayRef = useRef(day);
  dayRef.current = day;

  const load = useCallback(async () => {
    try {
      const [r, m, msg, up] = await Promise.all([
        supabase.from('hk_day_rooms').select('*').eq('day', day),
        supabase.from('hk_rooms').select('*'),
        supabase.from('hk_messages').select('*').eq('day', day).order('created_at', { ascending: false }),
        supabase.from('hk_uploads').select('*').eq('day', day).order('uploaded_at', { ascending: false }).limit(10),
      ]);
      if (dayRef.current !== day) return;
      const err = r.error || m.error || msg.error;
      if (err) throw err;
      setRows(r.data || []);
      setMaster(Object.fromEntries((m.data || []).map((x) => [x.room, x])));
      setMessages(msg.data || []);
      const ups = up.data || [];
      setLastUpload(ups.find((u) => !/\(charges\)$/.test(u.file_name || '')) || null);
      setLastCharges(ups.find((u) => /\(charges\)$/.test(u.file_name || '')) || null);
      setError(null);
      setLastSync(new Date());
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [day]);

  useEffect(() => {
    setLoading(true);
    load();
    const ch = supabase
      .channel(`hk-${day}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'hk_day_rooms', filter: `day=eq.${day}` }, (p) => {
        setRows((prev) => {
          if (p.eventType === 'DELETE') return prev.filter((x) => !(x.room === p.old.room));
          const i = prev.findIndex((x) => x.room === p.new.room);
          if (i === -1) return [...prev, p.new];
          const copy = prev.slice(); copy[i] = p.new; return copy;
        });
        setLastSync(new Date());
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'hk_messages', filter: `day=eq.${day}` }, (p) => {
        setMessages((prev) => {
          if (p.eventType === 'DELETE') return prev.filter((x) => x.id !== p.old.id);
          const i = prev.findIndex((x) => x.id === p.new.id);
          if (i === -1) return [p.new, ...prev];
          const copy = prev.slice(); copy[i] = p.new; return copy;
        });
        setLastSync(new Date());
      })
      .subscribe();
    const t = setInterval(() => { if (navigator.onLine) load(); }, 60000);
    return () => { clearInterval(t); supabase.removeChannel(ch); };
  }, [day, load]);

  const rooms = useMemo(() => rows.map((r) => toLiveRoom(r, master[r.room])), [rows, master]);

  /** Optimistic local patch so taps feel instant; realtime confirms. */
  const patchLocal = useCallback((room, patch) => {
    setRows((prev) => prev.map((x) => (x.room === room ? { ...x, ...patch } : x)));
  }, []);

  return { rooms, messages, lastUpload, lastCharges, loading, error, lastSync, reload: load, patchLocal };
}

export function nowMinutes(d = new Date()) {
  return d.getHours() * 60 + d.getMinutes();
}

export function timeAgo(iso, now = new Date()) {
  if (!iso) return '';
  const mins = Math.max(0, Math.round((now - new Date(iso)) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const h = Math.floor(mins / 60);
  return `${h} h ${mins % 60} min ago`;
}

export const hhmmOf = (iso) => (iso ? new Date(iso).toLocaleTimeString('en-IE', { hour: '2-digit', minute: '2-digit' }) : '');

/** Active housekeeping staff (hk_staff), in list order. */
export function useHkStaff() {
  const [staff, setStaff] = useState([]);
  useEffect(() => {
    let live = true;
    supabase.from('hk_staff').select('name, sort').eq('active', true).order('sort').then(({ data }) => {
      if (live && data) setStaff(data.map((x) => x.name));
    });
    return () => { live = false; };
  }, []);
  return staff;
}

export const firstName = (n) => String(n || '').split(' ')[0];

export const minutesBetween = (a, b) => (a && b ? Math.round((new Date(b) - new Date(a)) / 60000) : null);

/** Cleaning times for a day: rooms with a finish time, joined to room master. */
export async function fetchCleaningTimes(day) {
  const [r, m] = await Promise.all([
    supabase.from('hk_day_rooms').select('room, job, cleaner, started_at, done_at, status').eq('day', day).not('done_at', 'is', null),
    supabase.from('hk_rooms').select('room, type, premium'),
  ]);
  if (r.error) throw r.error;
  const master = Object.fromEntries((m.data || []).map((x) => [x.room, x]));
  return (r.data || []).map((x) => ({ ...x, type: master[x.room]?.type || '', premium: !!master[x.room]?.premium, mins: minutesBetween(x.started_at, x.done_at) }))
    .sort((a, b) => String(a.done_at).localeCompare(String(b.done_at)));
}
